import { Fragment } from 'react'

// splitTerms mirrors the backend `searchTerms` helper — lowercase, split on
// whitespace, drop duplicates, preserving first-appearance order.
export function splitTerms(q: string): string[] {
  if (!q) return []
  const seen = new Set<string>()
  const out: string[] = []
  for (const t of q.toLowerCase().split(/\s+/)) {
    if (!t) continue
    if (seen.has(t)) continue
    seen.add(t)
    out.push(t)
  }
  return out
}

// matchesTerms returns true when every term in `terms` appears as a substring of
// `text` (case-insensitive). This mirrors the backend `likeAllTermsClause` AND-of-
// LIKE semantics so client-side filters match the same rows the picker fetches —
// e.g. "frozen spinach" matches "frozen chopped spinach".
export function matchesTerms(text: string, terms: string[]): boolean {
  if (terms.length === 0) return true
  const lower = text.toLowerCase()
  return terms.every(t => lower.includes(t))
}

// Highlight walks `text` once and wraps every non-overlapping case-insensitive
// match of any term in <strong>. Earliest-starting match wins on overlap; on
// ties, the longer term wins so we don't break inside a longer hit.
export function Highlight({ text, terms }: { text: string; terms: string[] }) {
  if (!text || terms.length === 0) return <>{text}</>
  const lower = text.toLowerCase()
  const sorted = [...terms].sort((a, b) => b.length - a.length)
  const parts: Array<{ s: string; hit: boolean }> = []
  let i = 0
  while (i < text.length) {
    let bestLen = 0
    for (const t of sorted) {
      if (!t) continue
      if (lower.startsWith(t, i)) {
        if (t.length > bestLen) bestLen = t.length
      }
    }
    if (bestLen > 0) {
      parts.push({ s: text.slice(i, i + bestLen), hit: true })
      i += bestLen
    } else {
      const last = parts[parts.length - 1]
      if (last && !last.hit) {
        last.s += text[i]
      } else {
        parts.push({ s: text[i], hit: false })
      }
      i++
    }
  }
  return (
    <>
      {parts.map((p, idx) => (
        <Fragment key={idx}>
          {p.hit ? <strong>{p.s}</strong> : p.s}
        </Fragment>
      ))}
    </>
  )
}
